import React, { useEffect, useState } from 'react';

import {
  Box,
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { Prompt } from 'react-router-dom';

import { CheckCircle } from 'assets/assetsV1/iconComponents';

import { Button } from 'uiToolkitV1/Button';
import { TextBodyMedium, TextHeader3 } from 'uiToolkitV1/Typography';

interface Props {
  when?: boolean | undefined;
  navigate: (path: string) => void;
  shouldBlockNavigation: (location: Location) => boolean;
  onSave?: () => void;
}

// REFER: https://michaelchan-13570.medium.com/using-react-router-v4-prompt-with-custom-modal-component-ca839f5faf39
const RouteLeavingGuard = ({ when, navigate, shouldBlockNavigation, onSave }: Props) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [lastLocation, setLastLocation] = useState<Location | null>(null);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);

  const closeModal = () => {
    setLastLocation(null);
    setModalVisible(false);
    setConfirmedNavigation(false);
  };

  const handleBlockedNavigation = (nextLocation: Location): boolean => {
    if (!confirmedNavigation && shouldBlockNavigation(nextLocation)) {
      setModalVisible(true);
      setLastLocation(nextLocation);
      return false;
    }
    return true;
  };

  const handleConfirmNavigationClick = () => {
    if (onSave) {
      onSave();
    }
    setModalVisible(false);
    setConfirmedNavigation(true);
  };

  const handleDiscardNavigationClick = () => {
    setModalVisible(false);
    setConfirmedNavigation(true);
  };

  useEffect(() => {
    if (confirmedNavigation && lastLocation) {
      // Navigate to the previous blocked location with your navigate function
      navigate(lastLocation.pathname);
    }
  }, [confirmedNavigation, lastLocation]);

  return (
    <>
      {/* @ts-ignore */}
      <Prompt when={when} message={handleBlockedNavigation} />
      {/* Your own alert/dialog/modal component */}
      <Modal isOpen={modalVisible} onClose={closeModal}>
        <ModalOverlay />
        <ModalContent position="relative" maxH="204px" maxW="578px">
          <ModalHeader position="relative">
            <Flex alignItems="center">
              <CheckCircle boxSize="24px" color="#333333" mr="8px" />
              <TextHeader3 bold>Save Changes</TextHeader3>
            </Flex>
            {/* removing close button as it's causing issues on tab change in settings */}
            {/* <ModalCloseButton
              style={{
                position: 'absolute',
                left: '-3%',
                top: '-20%',
                backgroundColor: '#323846',
                color: 'white',
                borderRadius: '40px',
                width: '28px',
                height: '28px'
              }}
            /> */}
          </ModalHeader>
          <Divider opacity={1} color="#EBEFF3" />
          <ModalBody p="25px">
            <Box display="flex">
              <TextBodyMedium>
                You may lose the edits you made, if you switch to another tab without saving. Do you want to save your
                changes?{' '}
              </TextBodyMedium>
            </Box>
          </ModalBody>
          <ModalFooter
            sx={{
              background: '#F5F7F9',
              borderTop: '1px solid #CED7DF',
              borderRadius: '0px 0px 8px 8px',
            }}
          >
            <Button
              sx={{
                '&>p': {
                  marginLeft: 0,
                },
              }}
              mr="auto"
              variant="tertiary"
              onClick={handleDiscardNavigationClick}
            >
              Discard
            </Button>
            <Button onClick={handleConfirmNavigationClick}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
export { RouteLeavingGuard };
